import { z } from "zod";

import type { CommonProcedureTerminologyItem } from "@/src/models/documents/prior-authorization/prior-authorization";

export function normalizeCptCode(code: string | null | undefined): string {
  if (!code) return "";

  return code.replace(/\s+/g, "").trim();
}

export function normalizeCptModifier(modifier: string | null | undefined): string {
  if (!modifier) return "";

  const trimmed = modifier.replace(/\s+/g, "").trim();

  return trimmed.slice(0, 2).toUpperCase();
}

export function formatCPTCodeForDisplay(code: string | null | undefined, modifier?: string | null): string {
  const c = normalizeCptCode(code);
  const m = normalizeCptModifier(modifier ?? "");

  if (!c) return "";

  return m ? `${c}-${m}` : c;
}

export const cptSchema = z.object({
  id: z.string().uuid(),
  code: z
    .string()
    .min(1, "CPT code is required")
    .transform((val) => normalizeCptCode(val)),
  modifier: z
    .string()
    .optional()
    .transform((val) => normalizeCptModifier(val ?? "")),
  description: z.string().min(1, "Description is required"),
  searchText: z.string().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  deletedAt: z.date().nullable().optional(),
});

export type CPT = z.infer<typeof cptSchema>;

export function getCPTDisplayName(cpt: CPT): string {
  const formatted = formatCPTCodeForDisplay(cpt.code, cpt.modifier);

  return `${formatted} - ${cpt.description}`;
}

export function getCPTShortDisplayName(cpt: CPT): string {
  return formatCPTCodeForDisplay(cpt.code, cpt.modifier);
}

export function isLikelyCptCodeSearchQuery(query: string): boolean {
  const compact = query.replace(/\s+/g, "").trim();

  if (!compact) {
    return false;
  }

  return /^\d{4,7}(-[A-Za-z0-9]{1,2})?$/i.test(compact) || /^[A-Z]\d{4}(-[A-Za-z0-9]{1,2})?$/i.test(compact);
}

export function shouldMatchCptCodeWithEquality(code: string, modifier: string): boolean {
  const c = normalizeCptCode(code);

  if (!c) {
    return false;
  }

  if (/^\d{5}$/.test(c) || /^[A-Z]\d{4}$/i.test(c)) {
    return true;
  }

  return Boolean(modifier && /^\d{4,5}$/.test(c));
}

export function parseCptSearchQuery(query: string): { code: string; modifier: string } {
  const trimmed = query.trim();

  if (!trimmed) {
    return { code: "", modifier: "" };
  }

  const dashIdx = trimmed.indexOf("-");

  if (dashIdx > 0) {
    return {
      code: normalizeCptCode(trimmed.slice(0, dashIdx)),
      modifier: normalizeCptModifier(trimmed.slice(dashIdx + 1)),
    };
  }

  return { code: normalizeCptCode(trimmed), modifier: "" };
}

export function commonProcedureItemToCPT(item: CommonProcedureTerminologyItem): CPT {
  return {
    id: "",
    code: normalizeCptCode(item.code),
    modifier: normalizeCptModifier(item.modifier ?? ""),
    description: item.description ?? "",
  };
}

export function cptToCommonProcedureItem(cpt: CPT): CommonProcedureTerminologyItem {
  return {
    code: cpt.code,
    description: cpt.description,
    modifier: cpt.modifier || undefined,
  };
}
